import {randomFloor, range, wait, mergeDeep} from "../utils";
import EventHandlerUtility from "../utils/events";
import SocketConnect from "../utils/socket";

const PROBABILITIES_S = {
  'miss': 0.25,
  'single': 0.40,
  'double': 0.075,
  'triple': 0.04,
  'bull': 0.01,
  'double_bull': 0.01,
  'median_score': 23.36
}
const PROBABILITIES_M = {
  'miss': 0.1,
  'single': 0.675,
  'double': 0.275,
  'triple': 0.15,
  'bull': 0.075,
  'double_bull': 0.035,
  'median_score': 63.64
}
const PROBABILITIES_L = {
  'miss': 0.05,
  'single': 0.9,
  'double': 0.5,
  'triple': 0.375,
  'bull': 0.15,
  'double_bull': 0.1,
  'median_score': 121.54
}

export function clearLeg(leg = []) {
  let isNotEmptySeries = false
  return leg.reduceRight((data, series) => {
    if (isNotEmptySeries || Object.keys(series[0]).length || Object.keys(series[1]).length) {
      isNotEmptySeries = true
      data.push(series)
    }
    return data
  }, []).reverse()
}

export const isEmptyScore = (series, advanced_input) => (i) => {
  if (!series) return true
  if (series && series.length === 0) return true
  if (advanced_input || series[i].sectors) {
    return series[i].sectors?.length < 3 || series[i].sectors?.some(shot => !shot)
  }
  return series[i].score === undefined
}

export const isSeriesCompleted = (series, advanced_input) => !([0, 1].some(isEmptyScore(series, advanced_input)));

export const isSeriesEmpty = (series, advanced_input) => ([0, 1].every(isEmptyScore(series, advanced_input)));

export const layoutResizer = new EventHandlerUtility()

const SETTINGS_LOCAL_KEY = 'settings'

/*
* TODO:
*  - делаем функцию указывающую на полную блокировку
*
* */

class Base extends SocketConnect {
  // stream = null;
  urls = {}
  bot_processing = false
  correction_factor = 0

  constructor(ctx, data, urls) {
    super(ctx, urls.ws, {
      get: urls.game_data,
      post: urls.game_data,
    })
    this.urls = urls
    const {id} = this.set('game', data)
    this.set('status', 'loading')
    this.set('selected', [])

    this.set('settings', {
      advanced_input: this.getLocalStorage('', 'advanced_input') ?? false,
      fast_input: this.getLocalStorage('', 'fast_input') ?? false,
      full_screen: this.getLocalStorage('', 'full_screen') ?? false,
      with_video: this.getLocalStorage(id, 'with_video') ?? this.get('game').with_video,
      hidden_keyboard: false
    })

    document.addEventListener("fullscreenchange", () => {
      if (!document.fullscreenElement) {
        this.full_screen = document.fullscreenElement
      }
    });
  }

  listenChangeLayout(subscribe) {
    layoutResizer.registerHandler('resize', subscribe)
  }

  getLocalStorage(key, name) {
    const settings = JSON.parse(localStorage.getItem(SETTINGS_LOCAL_KEY + key)) || {}
    return settings[name]
  }

  getLocalSetting(key) {
    return this.get('settings')[key]
  }

  setLocalSetting(key, value) {
    const settings = mergeDeep({}, this.get('settings'), value)
    this.set('settings', settings);
    localStorage.setItem(SETTINGS_LOCAL_KEY + key, JSON.stringify(settings))
  }

  get advanced_input() {
    return this.getLocalSetting('advanced_input')
  }

  set advanced_input(advanced_input) {
    this.setLocalSetting('', {advanced_input})
  }

  get fast_input() {
    return this.getLocalSetting('fast_input')
  }

  set fast_input(fast_input) {
    this.setLocalSetting('', {fast_input})
    layoutResizer.trigger('resize')
  }

  get with_video() {
    return this.get('settings')['with_video']
  }

  set with_video(with_video) {
    this.setLocalSetting(this.get('game').id, {with_video})
    layoutResizer.trigger('resize')

  }

  get full_screen() {
    return this._full_screen
  }

  set full_screen(isFullScreen) {
    const docEl = document.documentElement;
    const fullEl = document.fullscreenElement
      || document.mozFullScreenElement
      || document.webkitFullscreenElement
      || document.msFullscreenElement;

    if (isFullScreen) {
      if (!fullEl) {
        const request = docEl.requestFullscreen
          || docEl.webkitRequestFullscreen
          || docEl.msRequestFullscreen;

        if (request) {
          try {
            request.call(docEl)
            this._full_screen = true
          } catch (e) {
            this._full_screen = false
          }
        } else {
          this._full_screen = false; // API отсутствует
        }
      } else {
        this._full_screen = true; // Уже в полноэкранном режиме
      }
    } else {
      if (fullEl) {
        const exit = document.exitFullscreen
          || document.webkitExitFullscreen
          || document.msExitFullscreen;

        if (exit) {
          try {
            exit.call(document)
            this._full_screen = false
          } catch (e) {
            this._full_screen = true
          }
        } else {
          this._full_screen = true; // API отсутствует
        }
      } else {
        this._full_screen = false; // Уже не в полноэкранном режиме
      }
    }
  }

  getWinnerNumberByLeg() {
    throw new Error('Not implement')
  }

  completedGame() {
    const {end_time} = this.get('game')
    return !!end_time || !!this.getWinnerNumberByLeg(this.data)
  }

  get data() {
    const legs = this.get('game').data
    return legs.at(-1)
  }

  get first() {
    const legs = this.get('game').data
    return this.calculateFirst(legs)
  }

  getPriorityHits() {
    throw new Error('Not implement')
  }

  /**
   * Проверяет возможен ли такой бросок
   * Например в 501 если человек перебрал очки, то сгорает вся серия очков
   * */
  checkBotShot(input) {
    return true
  }

  /**
   * Возвращает приоритезированный по уменьшению очков список бросков
   * [[sector, m], [sector, m] ... ]
   * */
  getPriorityBaseHits() {
    const sectors = [25, ...range(20, 1)]
    const hits = [];

    sectors.forEach(sector => {
      hits.push([sector, 1]);
      // сектора 25 с мультипликатором 3 не бывает
      if (sector === 25) {
        hits.push([25, 2]);
      } else {
        hits.push([sector, 2]);
        hits.push([sector, 3]);
      }
    });

    return hits.sort((a, b) => {
      const prodA = a[0] * a[1];
      const prodB = b[0] * b[1];
      // если сумма ровна сравниваем по сектору
      return prodA !== prodB ? prodB - prodA : b[0] - a[0]
    });
  }

  /**
   * Возвращет возмоный бросок из списка переданных в соответствии с переданными вероятностями
   * [sector, multi]
   * */
  getHitWithProb(hits, prob) {
    const probs = hits.map(([s, m]) => {
      if (s === 25 && m === 1) {
        return prob.sectors[1]?.[25] ?? PROBABILITIES_S.bull
      }
      if (s === 25 && m === 2) {
        return prob.sectors[2]?.[25] ?? PROBABILITIES_S.double_bull
      }
      if (m === 1) {
        return prob.sectors[1]?.[s] ?? PROBABILITIES_S.single
      }
      if (m === 2) {
        return prob.sectors[2]?.[s] ?? PROBABILITIES_S.double
      }
      if (m === 3) {
        return prob.sectors[3]?.[s] ?? PROBABILITIES_S.triple
      }
      return 0
    });

    const sumProb = probs.reduce((acc, cur) => acc + cur, 0);
    const tryRandom = randomFloor(0.001, 0.4 - this.correction_factor)
    // const adjustedRandom = tryRandom * (1 - this.correction_factor);
    const rand = tryRandom * sumProb;

    let sum = 0;

    const sector = hits.find((hit, i) => {
      sum += probs[i];
      return rand <= sum;
    })

    if (!sector) {
      if (this.correction_factor < 1) this.correction_factor += 0.1
      return this.getHitWithProb(hits, prob)
    }
    return sector
  }

  /**
   * Проверка на промах ботом
   * true - промазал, false - попал в дартс
   * */
  missHit(prob) {
    return Math.random() <= prob.sectors[1]?.[0] ?? PROBABILITIES_S.miss
  }

  /**
   * Добавляет коэффициент коррекции если есть между бросками и средним значением
   * */
  correctBot(prob) {
    const leg = this.data
    let lastScore = 0
    if (isEmptyScore(leg.at(-1))(1)) {
      if (isEmptyScore(leg.at(-2))(1)) {
        return
      } else {
        lastScore = leg.at(-2).at(1).score
      }
    } else {
      lastScore = leg.at(-1).at(1).score
    }

    const medianScore = prob.median_score ?? PROBABILITIES_S.median_score
    let diff = lastScore - medianScore;
    this.correction_factor = Math.min(Math.max(-diff / medianScore, -0.2), 0.2)
  }

  /**
   * Бот делает один бросок
   * возвращает сектор и множетель
   * [sector, multi]
   * */
  botShot() {
    const prob = this.getBotProb()

    // TODO
    this.correctBot(prob)

    if (!this.missHit(prob)) {
      return this.getHitWithProb(this.getPriorityHits(), prob)
    }
    return [0, 0]
  }

  /**
   * Возвращает данные по вероятностям попадания в чектора и сердний бросок
   * или возращзает загрушку
   * sectors" {
   *  [multi]: {
   *    [sector]: 0-1
   *  }
   * }
   * */
  getBotProb() {
    return window.BOT_PROB ?? {
      'sectors': {
        1: {},
        2: {},
        3: {}
      },
      'median_score': 0
    }
  }

  isBlockedSelected() {
    const {split_input, player_1, player_2} = this.get('game')

    if (!split_input) return false

    const PLAYER_ID = window.PLAYER_ID
    const [, p] = this.get('selected')
    const PLAYERS_SELECTED = {0: player_1.id, 1: player_2.id}

    if (PLAYERS_SELECTED[0] !== PLAYER_ID && PLAYERS_SELECTED[1] !== PLAYER_ID) return true

    return PLAYERS_SELECTED[p] !== window.PLAYER_ID
  }

  isBlockedNumber(number) {
    const {split_input, player_1, player_2} = this.get('game')

    if (!split_input) return false

    const PLAYER_ID = window.PLAYER_ID
    const PLAYERS_SELECTED = {0: player_1.id, 1: player_2.id}

    if (PLAYERS_SELECTED[0] !== PLAYER_ID && PLAYERS_SELECTED[1] !== PLAYER_ID) return true

    return PLAYERS_SELECTED[number] !== window.PLAYER_ID
  }

  isBlockedNotParticipation() {
    const {split_input, player_1, player_2} = this.get('game')

    if (!split_input) return false

    const PLAYER_ID = window.PLAYER_ID
    const PLAYERS_SELECTED = {0: player_1.id, 1: player_2.id}

    if (PLAYERS_SELECTED[0] !== PLAYER_ID && PLAYERS_SELECTED[1] !== PLAYER_ID) return true

    return false
  }

  /**
   * Возвращает с какой стороны играет пользователь
   * 0 - слева, 1 - справа, undefined если нельзя определить
   */
  getParticipantNumber() {
    const {player_1, player_2} = this.get('game')

    if (window.PLAYER_NUMBER !== undefined && player_1.id === player_2.id) {
      return this.get('selected')[1]
    }

    return window.PLAYER_NUMBER
  }

  /**
   * Проверяет ход бота сейчас или нет
   * */
  checkBotPlay() {
    const {mode} = this.get('game')
    if (mode === 'bot') {
      const [, p] = this.get('selected')
      const participant_number = this.getParticipantNumber()
      return participant_number !== p
    }
    return false
  }

  /**
   * Возможный ход(один бросок) бота, вызывается после загрузки данных или сохранения
   * */
  maybeBotPlay(isSave) {
    if (window.GAME_MODE !== 'play') return;
    if (this.completedGame()) return
    if (isSave) this.bot_processing = false
    if (this.bot_processing) return;

    if (!this.checkBotPlay()) return

    this.bot_processing = true

    let [i, p,] = this.get('selected')

    const [sector, m] = this.botShot()

    setTimeout(() => {
      if (this.checkBotShot(sector * m)) {
        this.sendSector(sector, m)
      } else {
        this.sendEmptySerial(i, p)
      }
    }, 500)
  }

  /**
   * Возвращает игрока котоырй выйграл
   * Игрока может не быть
   * */
  getWinnerPlayer() {
    const {player_1, player_2, winner_number} = this.get('game');
    const winner_number_player = {1: player_1, 2: player_2}
    if (winner_number) {
      return winner_number_player[winner_number]
    }
    return null
  }

  getWinnerPlayerByLeg(leg) {
    const {player_1, player_2} = this.get('game');
    const winner_number_player = {1: player_1, 2: player_2}
    const winner_number = this.getWinnerNumberByLeg(leg)
    return winner_number_player[winner_number]
  }

  calculateTotalScore(legs = []) {
    return legs.reduce((scores, leg) => {
      const winner_number = this.getWinnerNumberByLeg(leg)
      if (winner_number === 1) scores[0] += 1
      if (winner_number === 2) scores[1] += 1
      return scores
    }, [0, 0])
  }

  /**
   * Вычисляет сумму очков от переданых легов
   * */
  calculateScore(leg) {
    return leg.reduce((scores, series) => {
      scores[0] = scores[0] + (series[0].score || 0)
      scores[1] = scores[1] + (series[1].score || 0)
      return scores
    }, [0, 0])
  }

  calculateFirst(legs) {
    const {first} = this.get('game')
    return legs.reduce((f, c, i) => i ? +!f : f, first)
  }

  getInputSelected(i, p) {
    const leg = this.data
    return String(leg.at(i)?.at(p)?.score ?? '')
  }

  /**
   * Возвращает где сейчас должен находится селектор
   * [index, player, input, shot]
   * */
  getSelected() {
    const first = this.first
    const leg = this.data
    let shot = this.advanced_input ? 0 : null
    let p = first
    let i = leg.findIndex(series => {
      if ([0, 1].every(isEmptyScore(series, this.advanced_input))) return true

      const _p = [0, 1].findIndex(isEmptyScore(series, this.advanced_input))
      if (_p !== -1) {
        p = _p
        return true
      }
    })

    if (i === -1) {
      if (leg.length === this.maxSeries) {
        i = leg.length - 1
        p = +!first
      } else {
        i = leg.length
      }
    }

    const ss = leg.at(i)?.at(p);
    if (ss && ss.sectors) {
      shot = ss.sectors.findIndex((shot) => !shot)
      if (shot === -1) {
        const l = ss.sectors.length
        shot = l >= 3 ? 2 : l
      }
    }
    return [i, p, this.getInputSelected(i, p), shot]
  }

  restoreSelected() {
    this.set('selected', this.getSelected())
  }

  upSelected() {
    const selected = this.get('selected').slice()
    selected[0] = selected[0] - 1
    selected[2] = this.getInputSelected(selected[0], selected[1])
    this.setSelected(selected)
  }

  downSelected() {
    const selected = this.get('selected').slice()
    selected[0] = selected[0] + 1
    selected[2] = this.getInputSelected(selected[0], selected[1])
    this.setSelected(selected)
  }

  leftSelected() {
    const selected = this.get('selected').slice()
    if (selected[1] === 0) {
      selected[0] = selected[0] - 1
      selected[1] = 1
    } else {
      selected[1] = 0
      if (selected[3] !== null) {
        selected[3] = 2
      }
    }
    selected[2] = this.getInputSelected(selected[0], selected[1])
    this.setSelected(selected)
  }

  rightSelected() {
    const selected = this.get('selected').slice()
    if (selected[1] === 1) {
      selected[0] = selected[0] + 1
      selected[1] = 0
    } else {
      selected[1] = 1
      if (selected[3] !== null) {
        selected[3] = 0
      }
    }
    selected[2] = this.getInputSelected(selected[0], selected[1])
    this.setSelected(selected)
  }

  leftSelectedShot() {
    const selected = this.get('selected').slice()
    selected[3] = selected[3] - 1
    this.setSelected(selected)
  }

  rightSelectedShot() {
    const selected = this.get('selected').slice()
    selected[3] = selected[3] + 1
    this.setSelected(selected)
  }

  setSelected(selected, force) {
    if (this.bot_processing) return;

    if (force) {
      this.set('selected', selected)
      return;
    }

    const old_selected = this.get('selected').slice()
    const [i, p] = this.getSelected()
    const leg = this.data


    // пределы больше/меньше серия
    if (selected[0] > this.maxSeries - 1 || selected[0] < 0) {
      return
    }
    // debugger
    // сдвиг на одну серию ниже с не пустой
    if (selected[0] > Object.keys(leg[p] ?? {}).length && selected[0] > i) {
      return
    }

    // // // сдвиг на одну серию выше с пустой
    // debugger
    // if (selected[0] - old_selected[0] < 0 && !isSeriesEmpty(leg[old_selected[0]], this.advanced_input)) {
    //   this.set('selected', selected)
    //   return
    // }

    if (this.get('game').split_input) {
      // сдвиг больше чем на одну серию
      if (Math.abs(selected[0] - i) > 1) {
        return;
      }
      // сдвиг между игроками
      if (selected[1] !== old_selected[1]) {
        if (this.isBlockedNumber(selected[1])) return
      }
      // сдвиг по броскам
      if (selected[3] !== old_selected[2]) {
        if (this.isBlockedNumber(selected[1])) return
      }
    } else {
      if (selected[0] === i && selected[1] !== p) {
        if (old_selected[0] !== selected[0] || old_selected[1] !== selected[1]) {
          if (!selected[2]) {
            return;
          }
        }
      }
    }


    this.set('selected', selected)
  }

  async getVideoToken() {
    const response = await this.requestQueue(() => this.fetchRetry(this.urls.game_video_token, {
      headers: {
        "X-Requested-With": "XMLHttpRequest"
      },
    }))
    const data = await response.json()
    return data.token
  }

  afterFetch(data) {
    this.set('game', mergeDeep({}, this.get('game'), data));
    this.maybeBotPlay()
  }

  afterSave(data) {
    this.set('game', data);
    this.restoreSelected()
    this.maybeBotPlay(true)
  }

  preSave(body) {
    return mergeDeep({}, this.get('game'), body)
  }

  optimisticUpdate(body) {
    this.set('game', body)
  }

  onSocket(event) {
    switch (event.type) {
      case "stats_message":
      case "update":
        this.set('game', mergeDeep(this.get('game'), event.message));
        if (this.get('game').split_input) {
          this.restoreSelected()
        }
        break;
      default:
    }
  }

  /**
   * Сохранить значения(input) который находится в выбранной сейчас ячейки(selected)
   * */
  sendInput() {
    const [i, p, input] = this.get('selected');
    const leg = this.data
    let ss = leg.at(i)?.at(p) ?? {}

    if (input === String(ss.score ?? '')) {
      return false
    }

    // if (input !== '' && !this.validateScore(Number(input), p, i)) {
    //   return false
    // }

    if (input !== '') {
      ss.score = Number(input)
      delete ss.darts
    } else {
      ss = {}
    }

    return this.sendSeries(ss)
  }

  /**
   * Сохранить сектор и мультепликатор в выбранной сейчас ячейки(selected)
   * */
  sendSector(sector, m) {
    const [i, p, , shot] = this.get('selected');
    const leg = this.data
    let ss = leg.at(i)?.at(p) ?? {}

    if (sector === ss.sectors?.at(shot)?.at(0) && m === ss.sectors?.at(shot)?.at(0)) {
      return false
    }

    // if (sector != null && !this.validateScore((ss.score ?? 0) + sector * m, p, i)) {
    //   return false
    // }

    if (sector != null) {
      ss.sectors = ss.sectors || []
      ss.sectors[shot] = [sector, m]
    } else if (ss.sectors.length) {
      ss.sectors[shot] = null
      if (ss.sectors.length - 1 === shot) {
        ss.sectors = ss.sectors.slice(0, -1)
      }
      if (ss.sectors.every(ss => ss == null)) {
        ss = {}
      }
    } else {
      ss = {}
    }

    if (ss.sectors) {
      ss.score = ss.sectors.reduce((acc, cur) => (acc ?? 0) + (cur ? cur[0] * cur[1] : 0), undefined)
    }

    return this.sendSeries(ss)
  }

  /**
   * Сохранить всю серию из 3 дротиков
   * */
  sendSeries(ss) {
    const body = {};
    let [data, [score_1, score_2]] = this.getDataAndScore(ss)

    body.end_time = null

    // if (!body.start_time) {
    //   body.start_time = new Date().getTime()
    // }

    body.data = data
    body.score_1 = score_1
    body.score_2 = score_2

    this.save(this.preSave(body))

    return true
  }

  /**
   * Сохраняет пустую серию
   * */
  sendEmptySerial(i, p) {
    const leg = this.data
    let ss = leg.at(i)?.at(p) ?? {}
    ss.sectors = [[0, 0], [0, 0], [0, 0]]
    ss.score = 0
    return this.sendSeries(ss)
  }

  /**
   * Отменить последний выстрел в переданном индекси и игроке
   * */
  cancelShot(i, p) {
    const leg = this.data;

    const ss = leg.at(i)?.at(p)
    if (!ss) return;

    delete leg[leg.length - 1][0].winner
    delete leg[leg.length - 1][1].winner

    if (ss.sectors) {
      this.setSelected([i, p, this.getInputSelected(i, p), ss.sectors.length - 1], true)
      this.sendSector(null)
    } else {
      this.setSelected([i, p, '', null], true)
      this.sendInput()
    }
  }

  cancelLastShot() {
    const [i, p] = this.getLastPosition()
    if (p === undefined) return
    this.cancelShot(i, p)
  }

  cancelSeria(i, p) {
    const leg = this.data;
    delete leg[leg.length - 1][0].winner
    delete leg[leg.length - 1][1].winner
    this.setSelected([i, p, ''], true)
    this.sendSeries({})
  }

  cancelLastSeria() {
    const [i, p] = this.getLastPosition()
    if (p === undefined) return
    this.cancelSeria(i, p)
  }

  /**
   * Возвращает количество брошенных бротиков
   * */
  getDarts(leg) {
    return leg.reduce((darts, series) => {
      [...Array(2).keys()].forEach(p => {
        let d = 0
        if (series[p].darts !== undefined) {
          d = series[p].darts
        } else if (series[p].sectors) {
          d = series[p].sectors.length
        } else if (series[p].score !== undefined) {
          d = 3
        }
        darts[p] += d
      })
      return darts
    }, [0, 0])
  }

  getLastShotScore() {
    const leg = this.data;
    const lastSS = leg.at(-1);
    const lastLastSS = leg.at(-2);
    return [lastSS?.at(0)?.score ?? lastLastSS?.at(0)?.score, lastSS?.at(1)?.score ?? lastLastSS?.at(1)?.score]
  }

  getShotBySector(sector) {
    return this.data.reduce((shots, series) => {
      [...Array(2).keys()].forEach(p => {
        let s = 0
        if (series[p].sectors) {
          s = series[p].sectors.filter(([_sector]) => _sector === sector).length
        }
        shots[p] += s
      })
      return shots
    }, [0, 0])
  }
}

export default Base